/**
 * Ship Design Dialog — Create custom ship designs for a station.
 * Three-section layout: base class selector, tech selector, preview/create.
 */

import Phaser from 'phaser';
import {
  createDialogChrome, closeDialog,
  Colors, CSSColors, Fonts, FontSize, Radius,
} from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';
import { EventBus } from '../../utils/EventBus';

const PAD = 14;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('crimsonmandate_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

interface TechEntry {
  id: number;
  technologyId: number;
  name: string;
  description: string;
  tier: number;
  category: string;
}

const BASE_CLASSES = [
  { key: 'scout', label: 'Scout', cost: 50, desc: 'Fast recon vessel' },
  { key: 'fighter', label: 'Fighter', cost: 100, desc: 'Light combat ship' },
  { key: 'cruiser', label: 'Cruiser', cost: 200, desc: 'Heavy combat ship' },
  { key: 'space_tug', label: 'Space Tug', cost: 75, desc: 'Logistics vessel' },
];

export class ShipDesignDialog extends Phaser.GameObjects.Container {
  private contentW: number;

  private stationId: string;
  private selectedClassIdx = 0;
  private technologies: TechEntry[] = [];
  private selectedTechIds: Set<number> = new Set();
  private designName = '';
  private markupPct = 0;

  private contentContainer!: Phaser.GameObjects.Container;
  private previewContainer!: Phaser.GameObjects.Container;
  private nameInput?: HTMLInputElement;
  private dh: number;

  constructor(scene: Phaser.Scene, stationId: string) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 600;
    const dh = mobile ? 600 : 580;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.contentW = dw - PAD * 2;
    this.dh = dh;

    this.stationId = stationId;

    createDialogChrome(scene, this, dw, dh, {
      title: 'Create Ship Design',
      onClose: () => this.close(),
    });

    this.contentContainer = scene.add.container(PAD, 55);
    this.add(this.contentContainer);

    this.previewContainer = scene.add.container(PAD, dh - 170);
    this.add(this.previewContainer);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadTechnologies();
  }

  private close(): void {
    this.cleanupDOM();
    closeDialog(this.scene, this);
  }

  private cleanupDOM(): void {
    if (this.nameInput) {
      this.nameInput.remove();
      this.nameInput = undefined;
    }
    if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
  }

  private async loadTechnologies(): Promise<void> {
    try {
      const res = await fetch(`/api/stations/${this.stationId}/technologies`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        this.technologies = data.technologies || [];
      }
    } catch {
      // No techs available
    }
    this.render();
  }

  private render(): void {
    this.contentContainer.removeAll(true);
    this.previewContainer.removeAll(true);

    let y = 0;

    /* ── Section 1: Base class selector ──────────────────── */
    this.contentContainer.add(this.scene.add.text(0, y, 'BASE SHIP CLASS', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    for (let i = 0; i < BASE_CLASSES.length; i++) {
      const cls = BASE_CLASSES[i];
      const isSelected = i === this.selectedClassIdx;

      const card = this.scene.add.container(0, y);
      const bg = this.scene.add.graphics();
      bg.fillStyle(isSelected ? Colors.bgElevated : Colors.bgCard, isSelected ? 0.9 : 0.7);
      bg.fillRoundedRect(0, 0, this.contentW, 32, Radius.sm);
      bg.lineStyle(1, Colors.cyan, isSelected ? 0.4 : 0.06);
      bg.strokeRoundedRect(0, 0, this.contentW, 32, Radius.sm);
      card.add(bg);

      card.add(this.scene.add.text(10, 8, cls.label, {
        fontSize: FontSize.sm, color: isSelected ? '#FFFFFF' : '#8899BB', fontFamily: Fonts.heading,
      }));

      card.add(this.scene.add.text(120, 8, cls.desc, {
        fontSize: FontSize.xs, color: '#556688', fontFamily: Fonts.body,
      }));

      card.add(this.scene.add.text(this.contentW - 10, 8, `${cls.cost} CR`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
      }).setOrigin(1, 0));

      const idx = i;
      const zone = this.scene.add.zone(0, 0, this.contentW, 32).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.contentW, 32), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerdown', () => { this.selectedClassIdx = idx; this.render(); });
      zone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
      zone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
      card.add(zone);

      this.contentContainer.add(card);
      y += 36;
    }

    y += 8;

    /* ── Section 2: Technology selector ──────────────────── */
    this.contentContainer.add(this.scene.add.text(0, y, 'APPLIED TECHNOLOGIES', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    if (this.technologies.length === 0) {
      this.contentContainer.add(this.scene.add.text(0, y, 'No technologies researched yet', {
        fontSize: FontSize.xs, color: '#556688', fontFamily: Fonts.body,
      }));
      y += 20;
    } else {
      const maxShow = Math.min(this.technologies.length, 6);
      for (let i = 0; i < maxShow; i++) {
        const tech = this.technologies[i];
        const isChecked = this.selectedTechIds.has(tech.technologyId);

        const row = this.scene.add.container(0, y);

        // Checkbox
        const checkbox = this.scene.add.graphics();
        checkbox.lineStyle(1, Colors.cyan, 0.3);
        checkbox.strokeRect(0, 2, 14, 14);
        if (isChecked) {
          checkbox.fillStyle(Colors.cyan, 0.6);
          checkbox.fillRect(2, 4, 10, 10);
        }
        row.add(checkbox);

        row.add(this.scene.add.text(22, 2, tech.name, {
          fontSize: FontSize.xs, color: isChecked ? '#FFFFFF' : '#8899BB', fontFamily: Fonts.body,
        }));

        row.add(this.scene.add.text(22, 14, `T${tech.tier} ${tech.category}`, {
          fontSize: '9px', color: '#556688', fontFamily: Fonts.body,
        }));

        row.add(this.scene.add.text(this.contentW - 10, 2, '+25 CR', {
          fontSize: FontSize.xs, color: '#887755', fontFamily: Fonts.body,
        }).setOrigin(1, 0));

        const techId = tech.technologyId;
        const hitZone = this.scene.add.zone(0, 0, this.contentW, 28).setOrigin(0, 0);
        hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.contentW, 28), Phaser.Geom.Rectangle.Contains);
        hitZone.on('pointerdown', () => {
          if (this.selectedTechIds.has(techId)) {
            this.selectedTechIds.delete(techId);
          } else {
            this.selectedTechIds.add(techId);
          }
          this.render();
        });
        hitZone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
        hitZone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
        row.add(hitZone);

        this.contentContainer.add(row);
        y += 30;
      }
    }

    /* ── Section 3: Design name + preview + create ──────── */
    const cls = BASE_CLASSES[this.selectedClassIdx];
    const baseCost = cls.cost;
    const techCost = this.selectedTechIds.size * 25;
    const totalCost = baseCost + techCost;

    let py = 0;

    // Divider
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.06);
    divider.lineBetween(0, py, this.contentW, py);
    this.previewContainer.add(divider);
    py += 10;

    // Design name label
    this.previewContainer.add(this.scene.add.text(0, py, 'DESIGN NAME', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    py += 18;

    // Name input background (Phaser-rendered)
    const inputH = 30;
    const inputBg = this.scene.add.graphics();
    inputBg.fillStyle(0x0a1525, 0.8);
    inputBg.fillRoundedRect(0, py, this.contentW, inputH, Radius.sm);
    inputBg.lineStyle(1, Colors.cyan, 0.15);
    inputBg.strokeRoundedRect(0, py, this.contentW, inputH, Radius.sm);
    this.previewContainer.add(inputBg);

    // Create HTML input overlaid on the Phaser canvas
    this.createNameInput(py);
    py += inputH + 10;

    // Cost preview
    this.previewContainer.add(this.scene.add.text(0, py, `Base: ${cls.label} (${baseCost} CR) + Tech: ${this.selectedTechIds.size} (${techCost} CR)`, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    }));
    py += 16;

    this.previewContainer.add(this.scene.add.text(0, py, `Total Cost: ${totalCost} CR`, {
      fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
    }));

    // Create button
    const btnY = py - 4;
    const btnW = 140;
    const btnH = 34;
    const btnX = this.contentW - btnW;
    const btn = this.scene.add.container(btnX, btnY);

    const btnBg = this.scene.add.graphics();
    const drawBtn = (hover: boolean) => {
      btnBg.clear();
      btnBg.fillStyle(hover ? 0x226633 : 0x115522, 1);
      btnBg.fillRoundedRect(0, 0, btnW, btnH, Radius.sm);
      btnBg.lineStyle(1, 0x22aa44, 0.6);
      btnBg.strokeRoundedRect(0, 0, btnW, btnH, Radius.sm);
    };
    drawBtn(false);
    btn.add(btnBg);

    btn.add(this.scene.add.text(btnW / 2, btnH / 2, 'Create Design', {
      fontSize: FontSize.sm, color: '#ffffff', fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const btnZone = this.scene.add.zone(0, 0, btnW, btnH).setOrigin(0, 0);
    btnZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    btnZone.on('pointerover', () => { drawBtn(true); this.scene.input.setDefaultCursor('pointer'); });
    btnZone.on('pointerout', () => { drawBtn(false); this.scene.input.setDefaultCursor('default'); });
    btnZone.on('pointerdown', () => this.createDesign());
    btn.add(btnZone);

    this.previewContainer.add(btn);
  }

  private createNameInput(localY: number): void {
    // Remove old input if re-rendering
    if (this.nameInput) {
      this.nameInput.remove();
      this.nameInput = undefined;
    }

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const sx = canvasRect.width / canvas.width;
    const sy = canvasRect.height / canvas.height;

    // previewContainer is at (PAD, dh - 170), dialog is at this.x, this.y
    const absX = (this.x + PAD + 4) * sx + canvasRect.left;
    const absY = (this.y + (this.dh - 170) + localY + 2) * sy + canvasRect.top;
    const fieldW = (this.contentW - 8) * sx;
    const fieldH = 26 * sy;

    const cls = BASE_CLASSES[this.selectedClassIdx];

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = `${cls.label} Custom`;
    input.value = this.designName;
    input.maxLength = 30;
    input.style.cssText = `
      position:fixed; z-index:100001; pointer-events:auto;
      left:${absX}px; top:${absY}px;
      width:${fieldW}px; height:${fieldH}px;
      background:transparent; color:#e0e8ff; border:none; outline:none;
      font-family:${Fonts.body}; font-size:${12 * sy}px;
      caret-color:#00ffff; padding:0 8px;
    `;
    input.addEventListener('focus', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = false;
      EventBus.emit('chat-input-focused');
    });
    input.addEventListener('blur', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
      EventBus.emit('chat-input-unfocused');
    });
    input.addEventListener('input', () => {
      this.designName = input.value;
    });
    document.body.appendChild(input);
    this.nameInput = input;
  }

  private async createDesign(): Promise<void> {
    const cls = BASE_CLASSES[this.selectedClassIdx];
    const name = this.designName || `${cls.label} Custom`;
    const techIds = Array.from(this.selectedTechIds);

    try {
      const res = await fetch(`/api/stations/${this.stationId}/designs`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name,
          baseShipClass: cls.key,
          techIds,
          markupPct: this.markupPct,
        }),
      });

      if (res.ok) {
        console.log('[ShipDesign] Design created successfully');
        this.showFeedback(`Design "${name}" created!`, '#00FF88');
        this.scene.time.delayedCall(1200, () => this.close());
      } else {
        const data = await res.json();
        console.error('[ShipDesign] Failed to create design:', data.error);
        this.showFeedback(data.error || 'Failed to create design', '#FF4444');
      }
    } catch (err) {
      console.error('[ShipDesign] Error creating design:', err);
      this.showFeedback('Network error — please try again', '#FF4444');
    }
  }

  private showFeedback(msg: string, color: string): void {
    // Remove previous feedback
    const existing = this.getByName('feedback') as Phaser.GameObjects.Text;
    if (existing) existing.destroy();

    const text = this.scene.add.text(this.contentW / 2 + PAD, 42, msg, {
      fontSize: FontSize.sm, color, fontFamily: Fonts.body,
    });
    text.setOrigin(0.5);
    text.setName('feedback');
    this.add(text);
  }
}
